/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import moment from 'moment';
import {
    fetchToursByFacility,
    setTourSelectedCard,
} from '../../actions/toursDataActions';
import { filterTourList } from '../../actions/filtersActions';
import { ToursTab } from '../../components/FacilityPage/ToursTab.jsx';
import { useAccountType } from '../../hooks/accountHooks';
import useTourList from '../../hooks/useTourList';
import { fetchFacilityAvailability } from '../../actions/facilityAvailabilityActions';

export function ToursTabContainer({ handleTabChange }) {
    const { facilityId } = useParams();
    const { tourList: ticketList } = useTourList(facilityId);
    const [isMapViewOpen, setMapViewOpen] = useState(false);
    const dispatch = useDispatch();
    const { isCommAccount } = useAccountType();

    const accountType = useSelector((state) => state.timedEntry.accountType.accountType);
    const facilityData = useSelector((state) => state.timedEntry.facility);
    const toursData = useSelector((state) => state.timedEntry.tours);
    const toursImageData = useSelector((state) => state.timedEntry.tourImages);
    const toursRatingsData = useSelector((state) => state.timedEntry.tourRatings);
    const filterData = useSelector((state) => state.timedEntry.filters) || {};
    const { filteredTourTimes } = useSelector((state) => state.timedEntry.tourTimes);

    const { isLoading: isFacilityLoading } = facilityData;

    const {
        isLoading: areToursLoading,
        loadingStatus: toursLoadingStatus,
        selectedTourId,
    } = toursData;

    const { isLoading: areImagesLoading, data: tourImageMap } = toursImageData;

    const { isLoading: areRatingsLoading, data: tourRatingsMap } = toursRatingsData;

    const { date } = filterData;

    const selectedTourEvent = (tourId) => {
        dispatch(setTourSelectedCard(tourId));
    };

    useEffect(() => {
        dispatch(fetchToursByFacility(facilityId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, isCommAccount]);

    useEffect(() => {
        if (filteredTourTimes) {
            dispatch(filterTourList());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredTourTimes]);

    const tryFetchAgain = () => {
        dispatch(fetchToursByFacility(facilityId));
    };

    /**
     * On load: If we have a date but no filtered tour ids, then we re-fetch.
     * This can happen if the user sets the date filter, then navigates away and refreshes
     * the page
     */
    // useEffect(() => {
    //     if (!!date && (!filteredTourIds || isEmpty(filteredTourIds))) {
    //         dispatch(setDateFilter(null));
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);

    /**
     * When the account changes, we make the call to fetch availability to clear out any
     * dates and availability data the user might be seeing from the previous account type.
     * Doing this here because the calendar container where we normally do this isn't mounted
     * until the user clicks the "Add date..." filter button
     */
    useEffect(() => {
        if (facilityId) {
            dispatch(
                fetchFacilityAvailability({
                    facilityId,
                    date: date || moment(),
                    numberOfMonths: 1,
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountType, date, facilityId]);

    return (
        <ToursTab
            isMapLoading={isFacilityLoading || areToursLoading || areImagesLoading}
            isLoading={areToursLoading || areImagesLoading || areRatingsLoading}
            toursLoadingStatus={toursLoadingStatus}
            totalTourCount={ticketList?.length || 0}
            tours={ticketList}
            tourImageMap={tourImageMap}
            tourRatingsMap={tourRatingsMap}
            facilityData={facilityData}
            tryFetchAgain={tryFetchAgain}
            isMapViewOpen={isMapViewOpen}
            tourTimesById={filteredTourTimes}
            toggleMapView={() => {
                setMapViewOpen((prev) => !prev);
            }}
            selectedTourEvent={selectedTourEvent}
            selectedTourId={selectedTourId}
            handleTabChange={handleTabChange}
        />
    );
}

ToursTabContainer.propTypes = {
    handleTabChange: PropTypes.func,
};
